import { create } from "zustand"
import {
  fetchCurrentUser,
  login as apiLogin,
  register as apiRegister,
  updateCurrentUser,
  changePassword as apiChangePassword,
  type User,
  type LoginPayload,
  type RegisterPayload,
  type UpdateUserPayload,
  type ChangePasswordPayload,
} from "@/api/auth"

interface AuthState {
  user: User | null
  token: string | null
  loading: boolean
  error: string | null
  login: (payload: LoginPayload) => Promise<void>
  register: (payload: RegisterPayload) => Promise<void>
  logout: () => void
  initFromStorage: () => Promise<void>
  updateProfile: (payload: UpdateUserPayload) => Promise<void>
  changePassword: (payload: ChangePasswordPayload) => Promise<void>
}

const STORAGE_KEY = "ai_tutor_auth_token"

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  loading: false,
  error: null,

  async login(payload) {
    set({ loading: true, error: null })
    try {
      const tokenRes = await apiLogin(payload)
      const token = tokenRes.accessToken
      localStorage.setItem(STORAGE_KEY, token)
      const user = await fetchCurrentUser()
      set({ user, token, loading: false })
    } catch (e) {
      const msg = e instanceof Error ? e.message : "登录失败"
      set({ error: msg, loading: false })
      throw e
    }
  },

  async register(payload) {
    set({ loading: true, error: null })
    try {
      await apiRegister(payload)
      await get().login({ username: payload.username, password: payload.password })
    } catch (e) {
      const msg = e instanceof Error ? e.message : "注册失败"
      set({ error: msg, loading: false })
      throw e
    }
  },

  async updateProfile(payload) {
    const { token, user } = get()
    if (!token || !user) {
      set({ error: "未登录，无法更新个人信息" })
      throw new Error("未登录")
    }
    set({ loading: true, error: null })
    try {
      const updated = await updateCurrentUser(payload)
      set({ user: updated, loading: false })
    } catch (e) {
      const msg = e instanceof Error ? e.message : "更新个人信息失败"
      set({ error: msg, loading: false })
      throw e
    }
  },

  async changePassword(payload) {
    const { token } = get()
    if (!token) {
      set({ error: "未登录，无法修改密码" })
      throw new Error("未登录")
    }
    set({ loading: true, error: null })
    try {
      await apiChangePassword(payload)
      set({ loading: false })
    } catch (e) {
      const msg = e instanceof Error ? e.message : "修改密码失败"
      set({ error: msg, loading: false })
      throw e
    }
  },

  logout() {
    localStorage.removeItem(STORAGE_KEY)
    set({ user: null, token: null })
  },

  async initFromStorage() {
    const token = localStorage.getItem(STORAGE_KEY)
    if (!token) return
    set({ loading: true })
    try {
      const user = await fetchCurrentUser()
      set({ user, token, loading: false })
    } catch {
      localStorage.removeItem(STORAGE_KEY)
      set({ user: null, token: null, loading: false })
    }
  },
}))
